import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFinancial } from '../context/FinancialContext';
import { api } from '../services/api';

const SAVE_DEBOUNCE_MS = 2000;

/**
 * Loads the signed-in, active subscriber's saved plan from the API once per
 * login, then debounce-saves local state back whenever it changes. No-ops
 * for pending/inactive accounts — there's nothing to sync until they're
 * inside the app.
 */
export function useCloudSync() {
  const { user, isAuthenticated } = useAuth();
  const { state, dispatch } = useFinancial();
  const hasLoadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  const canSync = isAuthenticated && user?.subscriptionStatus === 'active';

  // Initial load, once per login session.
  useEffect(() => {
    if (!canSync || hasLoadedRef.current) return;

    hasLoadedRef.current = true;
    Promise.all([api.getProfile(), api.getGoals()])
      .then(([profileData, goalsData]) => {
        dispatch({
          type: 'HYDRATE_STATE',
          payload: {
            userProfile: profileData.userProfile,
            goals: goalsData.goals,
            retirementPlan: profileData.retirementPlan
          }
        });
      })
      .catch((err) => console.error('Failed to load saved plan:', err));
  }, [canSync, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) hasLoadedRef.current = false;
  }, [isAuthenticated]);

  // Debounced save on every state change.
  useEffect(() => {
    if (!canSync || !hasLoadedRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      api.updateProfile({
        userProfile: state.userProfile,
        retirementPlan: state.retirementPlan,
        riskProfile: state.riskProfile
      }).catch((err) => console.error('Failed to save profile:', err));

      api.saveGoals(state.goals).catch((err) => console.error('Failed to save goals:', err));
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.userProfile, state.goals, state.retirementPlan, state.riskProfile, canSync]);
}